<!--
  @component BibliographyPanel
  @description Zotero bibliography browser for Preparo Navigator

  Two-tab UI:
  - Local: shows locally saved bibliography items
  - Zotero: browse remote Zotero library/collections

  Items inserted into documents are automatically saved locally.
-->
<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import {
    allLibraries,
    selectedLibrary,
    selectedCollectionKey,
    flattenedCollections,
    items,
    totalItems,
    isInitialized,
    isLoading,
    isLoadingItems,
    zoteroError,
    zoteroActions,
    libraryCollectionOptions,
    loadLibraryItems,
    type LibraryCollectionOption,
  } from '@stores/zoteroStore';
  import { isZoteroConfigured, redirectToPortalSettings } from '@stores/authStore';
  import { selectedProject, selectedDoc } from '@stores/appStore';
  import { editorContent } from '@stores/editorStore';
  import { formatCreators, getItemsByKeys, type ZoteroItem } from '@services/zoteroService';
  import { bibliographyService, type StoredBibliography, type ZoteroItemData } from '@services/bibliographyService';
  import { citationService } from '@services/citationService';
  import { i18n } from '@stores/i18nStore';

  // Regex to match Pandoc citation keys: [@key] or [@key, p. 42]
  const CITATION_KEY_REGEX = /\[@([a-zA-Z0-9_:-]+)(?:,\s*[^\]]+)?\]/g;

  /**
   * Extract unique citation keys from markdown content
   */
  function extractCitationKeysFromMarkdown(markdown: string): string[] {
    const keys = new Set<string>();
    let match;
    CITATION_KEY_REGEX.lastIndex = 0;
    while ((match = CITATION_KEY_REGEX.exec(markdown)) !== null) {
      keys.add(match[1]);
    }
    return Array.from(keys);
  }

  // Sync state
  let isSyncingMissing = false;
  let lastSyncedDocId: string | null = null;
  let hasInitialSynced = false;

  // Reactive: sync when document changes
  $: if ($selectedDoc?.id && $selectedDoc.id !== lastSyncedDocId && $isZoteroConfigured && $isInitialized && $selectedLibrary) {
    lastSyncedDocId = $selectedDoc.id;
    // Small delay to let editorContent update
    setTimeout(() => syncMissingCitationsFromZotero(), 500);
  }

  // Reactive: sync on first Zotero initialization (for documents already open)
  $: if ($isInitialized && $selectedLibrary && $selectedDoc?.id && !hasInitialSynced) {
    hasInitialSynced = true;
    // Small delay to let editorContent update
    setTimeout(() => syncMissingCitationsFromZotero(), 500);
  }

  // Tab state
  type TabId = 'local' | 'zotero';
  let activeTab: TabId = 'local';
  let hasLoadedZoteroItems = false;

  // Load Zotero items when user switches to Zotero tab (lazy loading)
  $: if (activeTab === 'zotero' && $isInitialized && $selectedLibrary && !hasLoadedZoteroItems && $items.length === 0) {
    hasLoadedZoteroItems = true;
    loadLibraryItems($selectedLibrary.id, $selectedLibrary.type);
  }

  // Local bibliography state
  let storedBibliography: StoredBibliography | null = null;
  let isSavingBibliography = false;
  let lastSyncInfo: string | null = null;

  // Local items as array for display
  $: localItems = storedBibliography
    ? Object.values(storedBibliography.items)
    : [];

  const dispatch = createEventDispatcher<{
    insertCitation: { key: string; item: ZoteroItem };
  }>();

  /**
   * Load stored bibliography on mount
   */
  onMount(async () => {
    if ($selectedProject) {
      storedBibliography = await bibliographyService.getBibliography($selectedProject.id);
      if (storedBibliography) {
        // Register items with citation service (converts Zotero→CSL on-the-fly)
        const zoteroItems = Object.values(storedBibliography.items);
        await citationService.registerZoteroDataItems(zoteroItems);
        updateLastSyncInfo();

        // Start periodic sync if we have Zotero metadata
        if (storedBibliography.libraryId > 0) {
          bibliographyService.startPeriodicSync($selectedProject.id, (result) => {
            if (result.updated) {
              // Reload bibliography and update citation service
              bibliographyService.getBibliography($selectedProject!.id).then(async (bib) => {
                if (bib) {
                  storedBibliography = bib;
                  const items = Object.values(bib.items);
                  await citationService.registerZoteroDataItems(items);
                  updateLastSyncInfo();
                }
              });
            }
          });
        }
      }

      // Sync missing citations from Zotero to local (if Zotero is initialized)
      if ($isZoteroConfigured && $isInitialized && $selectedLibrary) {
        await syncMissingCitationsFromZotero();
      }
    }
  });

  /**
   * Stop periodic sync on destroy
   */
  onDestroy(() => {
    bibliographyService.stopPeriodicSync();
  });

  /**
   * Update last sync info display
   */
  function updateLastSyncInfo() {
    if (storedBibliography) {
      const date = new Date(storedBibliography.lastSync);
      lastSyncInfo = date.toLocaleTimeString();
    }
  }

  /**
   * Sync missing citations from Zotero to local bibliography
   * Extracts citation keys from current document, checks which are missing
   * from local bibliography, fetches them from Zotero, and saves locally.
   */
  async function syncMissingCitationsFromZotero(): Promise<void> {
    if (!$selectedProject || !$isZoteroConfigured || !$selectedLibrary) {
      return;
    }

    // Get current document markdown
    const markdown = $editorContent;
    if (!markdown) return;

    // Extract citation keys from document
    const docKeys = extractCitationKeysFromMarkdown(markdown);
    if (docKeys.length === 0) {
      console.log('[BibliographyPanel] No citations in document');
      return;
    }

    console.log('[BibliographyPanel] Found citation keys in document:', docKeys);

    // Get current local bibliography
    let bibliography = storedBibliography;
    if (!bibliography) {
      bibliography = {
        projectId: $selectedProject.id,
        libraryId: $selectedLibrary.id,
        libraryType: $selectedLibrary.type,
        collectionKey: null,
        collectionName: 'Local Bibliography',
        version: 0,
        lastSync: new Date().toISOString(),
        items: {},
      };
    }

    // Find keys that are missing from local bibliography
    const localKeys = new Set(Object.keys(bibliography.items));
    const missingKeys = docKeys.filter(key => !localKeys.has(key));

    if (missingKeys.length === 0) {
      console.log('[BibliographyPanel] All citations already in local bibliography');
      return;
    }

    console.log('[BibliographyPanel] Missing citations to fetch from Zotero:', missingKeys);

    // Fetch missing items from Zotero
    isSyncingMissing = true;
    try {
      const result = await getItemsByKeys(
        $selectedLibrary.id,
        $selectedLibrary.type,
        missingKeys
      );

      if (!result.success || !result.data || result.data.length === 0) {
        console.warn('[BibliographyPanel] Could not fetch missing items from Zotero:', result.error);
        return;
      }

      console.log('[BibliographyPanel] Fetched items from Zotero:', result.data.length);

      // Store items in native Zotero format (not CSL) for lossless round-trip editing
      for (const item of result.data) {
        // Store the native Zotero data format
        bibliography.items[item.key] = item.data as ZoteroItemData;
      }

      bibliography.lastSync = new Date().toISOString();

      // Save bibliography
      await bibliographyService.saveBibliography(bibliography);

      // Update local state
      storedBibliography = bibliography;

      // Register with citation service (converts Zotero→CSL on-the-fly)
      const zoteroItems = result.data.map(item => item.data);
      await citationService.registerZoteroDataItems(zoteroItems);
      updateLastSyncInfo();

      console.log('[BibliographyPanel] Missing citations synced to local bibliography');
    } catch (error) {
      console.error('[BibliographyPanel] Error syncing missing citations:', error);
    } finally {
      isSyncingMissing = false;
    }
  }

  /**
   * Save current Zotero selection as project bibliography
   */
  async function saveZoteroCollection() {
    if (!$selectedProject || !$selectedLibrary) return;

    isSavingBibliography = true;
    try {
      // Get collection name
      const collectionName = $selectedCollectionKey
        ? $flattenedCollections.find(c => c.key === $selectedCollectionKey)?.data.name || 'Collection'
        : $selectedLibrary.name;

      // Load and save bibliography
      storedBibliography = await bibliographyService.loadCollection(
        $selectedProject.id,
        $selectedLibrary.id,
        $selectedLibrary.type,
        $selectedCollectionKey,
        collectionName
      );

      // Register items with citation service (converts Zotero→CSL on-the-fly)
      const zoteroItems = Object.values(storedBibliography.items);
      await citationService.registerZoteroDataItems(zoteroItems);
      updateLastSyncInfo();

      // Start periodic sync
      bibliographyService.startPeriodicSync($selectedProject.id, (result) => {
        if (result.updated) {
          bibliographyService.getBibliography($selectedProject!.id).then(async (bib) => {
            if (bib) {
              storedBibliography = bib;
              const items = Object.values(bib.items);
              await citationService.registerZoteroDataItems(items);
              updateLastSyncInfo();
            }
          });
        }
      });

      // Switch to local tab to show saved items
      activeTab = 'local';
    } finally {
      isSavingBibliography = false;
    }
  }

  /**
   * Handle double-click on a Zotero item to insert citation
   * Also saves the item to local bibliography
   */
  async function handleZoteroItemDoubleClick(item: ZoteroItem) {
    // Dispatch event to insert citation
    dispatch('insertCitation', { key: item.key, item });

    // Auto-save item to local bibliography
    if ($selectedProject) {
      await saveItemToLocalBibliography(item);
    }
  }

  /**
   * Handle double-click on a local item to insert citation
   */
  function handleLocalItemDoubleClick(zoteroItem: ZoteroItemData) {
    // Create a minimal ZoteroItem-like object for the event
    dispatch('insertCitation', {
      key: zoteroItem.key || '',
      item: { key: zoteroItem.key || '', data: zoteroItem } as ZoteroItem
    });
  }

  /**
   * Save a single Zotero item to the local bibliography
   */
  async function saveItemToLocalBibliography(item: ZoteroItem) {
    if (!$selectedProject) return;

    try {
      let bibliography = await bibliographyService.getBibliography($selectedProject.id);

      if (!bibliography) {
        // Create new bibliography
        bibliography = {
          projectId: $selectedProject.id,
          libraryId: $selectedLibrary?.id || 0,
          libraryType: $selectedLibrary?.type || 'user',
          collectionKey: null,
          collectionName: 'Local Bibliography',
          version: 0,
          lastSync: new Date().toISOString(),
          items: {},
        };
      }

      // Store item in native Zotero format for lossless round-trip editing
      bibliography.items[item.key] = item.data as ZoteroItemData;
      bibliography.lastSync = new Date().toISOString();

      // Save bibliography
      await bibliographyService.saveBibliography(bibliography);

      // Update local state
      storedBibliography = bibliography;

      // Register with citation service (converts Zotero→CSL on-the-fly)
      await citationService.registerZoteroDataItems([item.data]);

      console.log('[BibliographyPanel] Item saved to local bibliography:', item.key);
    } catch (error) {
      console.error('[BibliographyPanel] Failed to save item:', error);
    }
  }

  // Handle selection change in combined selector
  function handleSelectionChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const value = select.value;

    if (!value) return;

    const parts = value.split(':');
    const optionType = parts[0]; // 'library' or 'collection'
    const libraryType = parts[1] as 'user' | 'group';
    const libraryId = parseInt(parts[2], 10);

    if (optionType === 'library') {
      // Select library and load its items
      const library = $allLibraries.find(l => l.type === libraryType && l.id === libraryId);
      if (library) {
        zoteroActions.selectLibraryWithItems(library);
      }
    } else if (optionType === 'collection') {
      const collectionKey = parts[3];
      // Make sure we have the right library selected first
      const library = $allLibraries.find(l => l.type === libraryType && l.id === libraryId);
      if (library) {
        // If library changed, select it first (collections will already be loaded)
        if ($selectedLibrary?.id !== libraryId || $selectedLibrary?.type !== libraryType) {
          zoteroActions.selectLibrary(library);
        }
        // Then select collection
        zoteroActions.selectCollection(collectionKey);
      }
    }
  }

  // Get current selection value
  $: currentValue = (() => {
    if (!$selectedLibrary) return '';
    if ($selectedCollectionKey) {
      return `collection:${$selectedLibrary.type}:${$selectedLibrary.id}:${$selectedCollectionKey}`;
    }
    return `library:${$selectedLibrary.type}:${$selectedLibrary.id}`;
  })();
</script>

<div class="bibliography-panel">
  <!-- Tab bar -->
  <div class="tab-bar">
    <button
      class="tab-btn"
      class:active={activeTab === 'local'}
      on:click={() => activeTab = 'local'}
    >
      {$i18n('bibliography.tabLocal')}
      {#if localItems.length > 0}
        <span class="tab-count">{localItems.length}</span>
      {/if}
    </button>
    <button
      class="tab-btn"
      class:active={activeTab === 'zotero'}
      on:click={() => activeTab = 'zotero'}
    >
      {$i18n('bibliography.tabZotero')}
    </button>
  </div>

  <!-- Tab content -->
  <div class="tab-content">
    {#if activeTab === 'local'}
      <!-- LOCAL TAB -->
      {#if isSyncingMissing}
        <div class="bib-loading">
          <div class="spinner"></div>
          <p>{$i18n('bibliography.syncingMissing')}</p>
        </div>
      {:else if !storedBibliography || localItems.length === 0}
        <div class="bib-empty">
          <p class="bib-empty-text">{$i18n('bibliography.localEmpty')}</p>
          <p class="bib-empty-hint">{$i18n('bibliography.localEmptyHint')}</p>
        </div>
      {:else}
        {#if lastSyncInfo}
          <div class="bib-sync-info">
            {$i18n('bibliography.lastSync')}: {lastSyncInfo}
          </div>
        {/if}

        <!-- Local items list -->
        <div class="bib-items">
          <ul class="item-list">
            {#each localItems as item (item.key)}
              <li
                class="item-entry"
                on:dblclick={() => handleLocalItemDoubleClick(item)}
                title={$i18n('bibliography.doubleClickToInsert')}
              >
                <span class="item-author">
                  {#if item.creators && item.creators.length > 0}
                    {item.creators.map(c => c.lastName || c.name || '').filter(Boolean).join(', ')}
                  {:else}
                    —
                  {/if}
                </span>
                <span class="item-title">{item.title || $i18n('bibliography.untitled')}</span>
              </li>
            {/each}
          </ul>
        </div>
      {/if}

    {:else}
      <!-- ZOTERO TAB -->
      {#if !$isZoteroConfigured}
        <!-- Zotero not configured -->
        <div class="bib-empty">
          <p class="bib-empty-text">{$i18n('bibliography.notConfigured')}</p>
          <button class="bib-configure-btn" on:click={redirectToPortalSettings}>
            {$i18n('bibliography.configureInPortal')}
          </button>
        </div>
      {:else if $isLoading}
        <!-- Initial loading -->
        <div class="bib-loading">
          <div class="spinner"></div>
          <p>{$i18n('bibliography.loading')}</p>
        </div>
      {:else if $zoteroError}
        <!-- Error state -->
        <div class="bib-error">
          <p class="bib-error-text">{$zoteroError}</p>
          <button class="bib-retry-btn" on:click={() => zoteroActions.init()}>
            {$i18n('bibliography.retry')}
          </button>
        </div>
      {:else if !$isInitialized}
        <!-- Not yet initialized -->
        <div class="bib-loading">
          <div class="spinner"></div>
          <p>{$i18n('bibliography.initializing')}</p>
        </div>
      {:else}
        <!-- Combined library/collection selector -->
        <div class="bib-selector">
          <select value={currentValue} on:change={handleSelectionChange} class="combined-select">
            {#each $libraryCollectionOptions as option (option.value)}
              <option value={option.value} style="padding-left: {option.level * 12}px">
                {option.level > 0 ? '\u00A0'.repeat(option.level * 2) + '└ ' : ''}{option.label}
              </option>
            {/each}
          </select>

          <!-- Save collection button -->
          {#if $selectedProject && $selectedLibrary && $items.length > 0}
            <button
              class="bib-save-btn"
              on:click={saveZoteroCollection}
              disabled={isSavingBibliography}
              title={$i18n('bibliography.saveCollection')}
            >
              {#if isSavingBibliography}
                <span class="spinner-tiny"></span>
              {:else}
                ⬇
              {/if}
            </button>
          {/if}
        </div>

        <!-- Zotero items list -->
        <div class="bib-items">
          {#if $isLoadingItems}
            <div class="bib-loading-small">
              <div class="spinner-small"></div>
            </div>
          {:else if $items.length === 0}
            <div class="bib-empty-section">
              <p>{$i18n('bibliography.noItems')}</p>
            </div>
          {:else}
            <ul class="item-list">
              {#each $items as item (item.key)}
                <li
                  class="item-entry"
                  on:dblclick={() => handleZoteroItemDoubleClick(item)}
                  title={$i18n('bibliography.doubleClickToInsert')}
                >
                  <span class="item-author">{formatCreators(item.data.creators, 2)}</span>
                  <span class="item-title">{item.data.title || $i18n('bibliography.untitled')}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .bibliography-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  /* Tab bar */
  .tab-bar {
    display: flex;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
  }

  .tab-btn {
    flex: 1;
    padding: 0.5rem 0.75rem;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }

  .tab-btn:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .tab-btn.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  .tab-count {
    font-size: 0.65rem;
    background: var(--accent-color);
    color: white;
    padding: 0.1rem 0.35rem;
    border-radius: 8px;
    min-width: 1.2rem;
    text-align: center;
  }

  .tab-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* Loading states */
  .bib-loading,
  .bib-loading-small {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    color: var(--text-muted);
    font-size: 0.8rem;
  }

  .bib-loading {
    height: 100%;
  }

  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--border-color, #e5e7eb);
    border-top-color: var(--accent-color, #3b82f6);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 0.5rem;
  }

  .spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-color, #e5e7eb);
    border-top-color: var(--accent-color, #3b82f6);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Error and empty states */
  .bib-error,
  .bib-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 1rem;
    text-align: center;
  }

  .bib-error-text {
    color: var(--error-color, #dc2626);
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }

  .bib-empty-text {
    color: var(--text-muted);
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }

  .bib-empty-hint {
    color: var(--text-muted);
    font-size: 0.7rem;
    opacity: 0.7;
  }

  .bib-retry-btn,
  .bib-configure-btn {
    padding: 0.4rem 0.8rem;
    background: var(--accent-color, #3b82f6);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .bib-retry-btn:hover,
  .bib-configure-btn:hover {
    background: var(--accent-color-hover, #2563eb);
  }

  .bib-empty-section {
    padding: 1rem;
    color: var(--text-muted);
    font-size: 0.75rem;
    text-align: center;
  }

  .bib-sync-info {
    padding: 0.25rem 0.5rem;
    font-size: 0.65rem;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-color);
  }

  /* Combined selector */
  .bib-selector {
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    gap: 0.35rem;
    align-items: center;
  }

  .combined-select {
    flex: 1;
    padding: 0.4rem 0.5rem;
    background: var(--panel-background, white);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.8rem;
    color: var(--text-primary);
    cursor: pointer;
  }

  .combined-select:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .combined-select option {
    padding: 0.25rem 0.5rem;
  }

  .bib-save-btn {
    padding: 0.35rem 0.5rem;
    background: var(--accent-color, #3b82f6);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 0.2s;
    min-width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bib-save-btn:hover:not(:disabled) {
    background: var(--accent-color-hover, #2563eb);
  }

  .bib-save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner-tiny {
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  /* Items list */
  .bib-items {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .item-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .item-entry {
    display: flex;
    flex-direction: column;
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid var(--border-color-light, #f0f0f0);
    cursor: pointer;
    transition: background 0.15s;
    user-select: none;
  }

  .item-entry:hover {
    background: var(--hover-background, #f5f5f5);
  }

  .item-entry:active {
    background: var(--active-background, #e8e8e8);
  }

  .item-author {
    font-size: 0.7rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0.15rem;
  }

  .item-title {
    font-size: 0.8rem;
    color: var(--text-primary);
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
